/**
 * Purpose: Client-side image compression and normalization for uploads (incl. iPhone HEIC/large JPEG).
 * Inputs: File from input; maxSizeMB and maxDimension options.
 * Outputs: JPEG File suitable for API upload validation.
 */

const ALLOWED_INPUT_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'heic',
  'heif',
]);

export function isAllowedImageFile(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  if (type.startsWith('image/') && type !== 'image/svg+xml') return true;
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
  return ALLOWED_INPUT_EXTENSIONS.has(ext);
}

function jpegFileName(originalName) {
  const base = (originalName || 'photo').replace(/\.[^.]+$/, '');
  return `${base}.jpg`;
}

/**
 * Compress and normalize an image to JPEG for upload.
 * iPhone photos (HEIC, large JPEG) are decoded via canvas and re-encoded as JPEG.
 */
export function compressImageToJpeg(file, { maxSizeMB = 1, maxDimension = 2048 } = {}) {
  return new Promise((resolve, reject) => {
    if (!isAllowedImageFile(file)) {
      reject(new Error('Please choose a photo (JPEG, PNG, WEBP, or HEIC). SVG is not supported.'));
      return;
    }

    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(
        new Error(
          'Could not read this photo. Try again or pick a different image from your gallery.',
        ),
      );
    };

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      let { width, height } = img;
      const scale = Math.min(1, maxDimension / Math.max(width, height, 1));
      width = Math.max(1, Math.round(width * scale));
      height = Math.max(1, Math.round(height * scale));

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      canvas.width = width;
      canvas.height = height;
      ctx.drawImage(img, 0, 0, width, height);

      let quality = 0.85;

      const tryEncode = () => {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('Could not process this photo. Please try another image.'));
              return;
            }
            if (blob.size <= maxSizeBytes || quality <= 0.35) {
              const out = new File([blob], jpegFileName(file.name), {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve({
                file: out,
                originalSize: file.size,
                compressedSize: blob.size,
                compressionRatio:
                  file.size > 0
                    ? Number((((file.size - blob.size) / file.size) * 100).toFixed(1))
                    : 0,
              });
              return;
            }
            quality -= 0.08;
            tryEncode();
          },
          'image/jpeg',
          quality,
        );
      };

      tryEncode();
    };

    img.src = objectUrl;
  });
}

/** Compress when file is large or from formats that need normalization for the API. */
export function shouldCompressImageFile(file) {
  if (!file) return false;
  const type = (file.type || '').toLowerCase();
  const ext = (file.name || '').split('.').pop()?.toLowerCase() || '';
  if (ext === 'heic' || ext === 'heif' || type.includes('heic') || type.includes('heif')) {
    return true;
  }
  if (file.size > 1024 * 1024) return true;
  if (type && !['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(type)) {
    return true;
  }
  return false;
}

export async function prepareImagesForUpload(files, options = {}) {
  const results = [];
  for (const file of files) {
    if (shouldCompressImageFile(file)) {
      results.push(await compressImageToJpeg(file, options));
    } else {
      results.push({
        file,
        originalSize: file.size,
        compressedSize: file.size,
        compressionRatio: 0,
      });
    }
  }
  return results;
}
